import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Eyes Test ToM — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/eyes-test-tom.md (RMET — Baron-Cohen 2001).
 *   E1 : tier 1 (distracteurs nets) — initiation au format.
 *   E2 : tier 2 (distracteurs proches) — début de discrimination.
 *   E3 : tier 3 (distracteurs plausibles) — calibration baseline.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { itemsByDifficulty } from './corpus';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'eyes_test_tom.demo.step1_title',
        hintKey: 'eyes_test_tom.demo.step1_hint',
        difficulty: 1,
    },
    {
        idx: 2,
        titleKey: 'eyes_test_tom.demo.step2_title',
        hintKey: 'eyes_test_tom.demo.step2_hint',
        difficulty: 2,
    },
    {
        idx: 3,
        titleKey: 'eyes_test_tom.demo.step3_title',
        hintKey: 'eyes_test_tom.demo.step3_hint',
        difficulty: 3,
    },
];
const STAGE_WIDTH = 280;
function pickDemoItem(difficulty) {
    const pool = itemsByDifficulty(difficulty);
    // Premier item du tier (déterministe pour FTUE).
    return pool[0];
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('awaiting');
    const [pickedIdx, setPickedIdx] = useState(null);
    const step = DEMO_STEPS[stepIdx];
    const item = pickDemoItem(step.difficulty);
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pickedIdx === item.correctIdx;
    const handlePick = (idx) => {
        if (phase !== 'awaiting')
            return;
        setPickedIdx(idx);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('awaiting');
        setPickedIdx(null);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('eyes_test_tom.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "flex flex-col items-center justify-center gap-s3 border border-cyan-border-faint bg-cyan-surface p-s4", style: { width: STAGE_WIDTH }, children: [_jsx("span", { className: "select-none text-[36px]", "aria-hidden": true, children: item.emoji }), _jsx("p", { className: "text-center text-[12.5px] leading-5 text-cyan-text", children: item.descriptor })] }), _jsx("div", { className: "grid w-full grid-cols-2 gap-s3", style: { maxWidth: STAGE_WIDTH }, children: item.choices.map((label, idx) => {
                    let extra = 'border border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-border-faint';
                    if (phase === 'feedback') {
                        if (idx === item.correctIdx)
                            extra = 'bg-cyan-core text-cyan-surface';
                        else if (idx === pickedIdx)
                            extra = 'bg-cyan-media text-cyan-surface';
                        else
                            extra = 'border border-cyan-border-faint bg-cyan-surface text-cyan-text-secondary opacity-60';
                    }
                    return (_jsx("button", { type: "button", onClick: () => handlePick(idx), disabled: phase === 'feedback', className: [
                            'px-s3 py-s2 text-[13.5px] font-medium transition duration-feedback ease-soft-wind active:scale-95 disabled:cursor-not-allowed',
                            extra,
                        ].join(' '), children: label }, `${label}-${idx}`));
                }) }), phase === 'feedback' && (_jsxs("p", { className: [
                    'max-w-[320px] text-[12.5px]',
                    correct ? 'text-cyan-core' : 'text-cyan-media',
                ].join(' '), children: [correct
                        ? t('eyes_test_tom.feedback.correct')
                        : t('eyes_test_tom.feedback.incorrect', { correct_label: item.choices[item.correctIdx] }), ' · ', _jsx("span", { className: "text-cyan-text-secondary", children: item.indices })] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map